import {Helmet, HelmetProvider} from '@dr.pogodin/react-helmet';
import {i18n} from '@online-offer/i18n';
import {Disclaimer, DisclaimerPrint} from '@online-offer/shared';
import {Box, Center, Show, Space, Stack, VStack} from 'platform/foundation';

import {Route, Routes} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {AdvertisementDescription} from './(sections)/AdvertisementDescription/AdvertisementDescription';
import {AdvertisementDescriptionPrint} from './(sections)/AdvertisementDescription/AdvertisementDescription.print';
import {AlternativeDetail} from './(sections)/AlternativeDetail/AlternativeDetail';
import {AlternativesComparison} from './(sections)/AlternativesComparison/AlternativesComparison';
import {CarPass} from './(sections)/CarPass/CarPass';
import {CarPassPrint} from './(sections)/CarPass/CarPass.print';
import {CebiaAutotracer} from './(sections)/CebiaAutotracer/CebiaAutotracer';
import {Certificate} from './(sections)/Certificate/Certificate';
import {CertificatePrint} from './(sections)/Certificate/Certificate.print';
import {Details} from './(sections)/Details/Details';
import {DetailsPrint} from './(sections)/Details/Details.print';
import {ElectricalParameters} from './(sections)/ElectricalParameters/ElectricalParameters';
import {ElectricalParametersPrint} from './(sections)/ElectricalParameters/ElectricalParameters.print';
import {ErrorSection} from './(sections)/ErrorSection/ErrorSection';
import {Features} from './(sections)/Features/Features';
import {FeaturesPrint} from './(sections)/Features/Features.print';
import {FinancialProductPreferenceProvider} from './(sections)/FinancialProducts/FinancialProductPreferenceProvider';
import {FinancialProducts} from './(sections)/FinancialProducts/FinancialProducts';
import {FinancialProductsPrint} from './(sections)/FinancialProducts/FinancialProducts.print';
import {Hero} from './(sections)/Hero/Hero';
import {HeroPrint} from './(sections)/Hero/Hero.print';
import {ReservationCardPrint} from './(sections)/ReservationCard/ReservationCard.print';
import {SellerCard} from './(sections)/SellerCard/SellerCard';
import {SellerCardPrint} from './(sections)/SellerCard/SellerCard.print';
import {Sidebar} from './(sections)/Sidebar/Sidebar';
import {Specification} from './(sections)/Specification/Specification';
import {SpecificationPrint} from './(sections)/Specification/Specification.print';
import {TechnicalParameters} from './(sections)/TechnicalParameters/TechnicalParameters';
import {TechnicalParametersPrint} from './(sections)/TechnicalParameters/TechnicalParameters.print';
import {Warranty} from './(sections)/Warranty/Warranty';
import {WarrantyPrint} from './(sections)/Warranty/Warranty.print';
import {AlternativesStrip} from './components/AlternativesStrip/AlternativesStrip';
import {ApplicationInitializeBoundary} from './components/ApplicationInitializeBoundary/ApplicationInitializeBoundary';
import {Footer} from './components/Footer/Footer';
import {Navigation} from './components/Navigation/Navigation';
import {APPLICATION_NAME} from './consts/applicationName';
import {useGetOnlineOfferData} from './hooks/useGetOnlineOfferData';
import {useViewedAlternative} from './hooks/useViewedAlternative';

const SIDEBAR_STICKY_OFFSET = 4;

export function App() {
  return (
    <Routes>
      <Route element={<ApplicationInitializeBoundary />}>
        <Route path=":offerId/" element={<OnlineOffer />} />
        <Route path=":offerId/compare" element={<OnlineOfferComparison />} />
        <Route path=":offerId/pdf" element={<OnlineOfferPrint />} />
        <Route path=":offerId/buying" element={<OnlineOffer />} />
        <Route path=":offerId/buying/pdf" element={<OnlineOfferPrint />} />
      </Route>
      <Route path="*" element={<ErrorSection />} />
    </Routes>
  );
}

function OnlineOfferComparison() {
  const {disclaimer, offerAlternatives} = useGetOnlineOfferData();

  // A legacy offer has nothing to compare: the link falls back to the offer itself.
  if (isNil(offerAlternatives)) {
    return <OnlineOffer />;
  }

  return (
    <HelmetProvider>
      <Helmet titleTemplate={`%s • ${APPLICATION_NAME}`} htmlAttributes={{lang: i18n.language}}>
        <meta name="description" content={APPLICATION_NAME} />
      </Helmet>

      <Navigation />
      <AlternativesComparison />
      <Center>
        <Box flexGrow={1} paddingHorizontal={[3, 8, 20, 20]} maxWidth={440}>
          <SellerCard />
          {/* Without a deal vehicle this page is the whole offer, so it carries the disclaimer. */}
          <Disclaimer disclaimer={disclaimer} />
        </Box>
      </Center>
      <Footer />
    </HelmetProvider>
  );
}

function OnlineOffer() {
  const {disclaimer, vehicle, offerAlternatives} = useGetOnlineOfferData();
  const {viewedAlternative} = useViewedAlternative();

  // No deal vehicle yet: the alternatives are the whole offer. Without them there is nothing to show.
  if (isNil(vehicle)) {
    return isNotNil(offerAlternatives) ? <OnlineOfferComparison /> : <ErrorSection />;
  }

  return (
    <HelmetProvider>
      <Helmet titleTemplate={`%s • ${APPLICATION_NAME}`} htmlAttributes={{lang: i18n.language}}>
        <meta name="description" content={APPLICATION_NAME} />
      </Helmet>

      <Navigation />

      <AlternativesStrip />

      {isNotNil(viewedAlternative) ? (
        <AlternativeDetail key={viewedAlternative.id} alternative={viewedAlternative} />
      ) : (
        <DealVehicleOffer disclaimer={disclaimer} />
      )}
      <Footer />
    </HelmetProvider>
  );
}

interface DealVehicleOfferProps {
  disclaimer: ReturnType<typeof useGetOnlineOfferData>['disclaimer'];
}

function DealVehicleOffer(props: DealVehicleOfferProps) {
  return (
    // The financing section and the price card show one customer choice.
    <FinancialProductPreferenceProvider>
      <Hero />

      <Space vertical={[6, 6, 11]} />

      <Center>
        <Box flexGrow={1} paddingHorizontal={[3, 8, 20, 20]} maxWidth={440}>
          <Stack direction={['column-reverse', 'column-reverse', 'row']} spacing={8} width="100%">
            <VStack width="100%">
              <Specification />
              <CarPass />
              <CebiaAutotracer />
              <Details />
              <ElectricalParameters />
              <AdvertisementDescription />
              <Warranty />
              <Features />
              <Certificate />
              <FinancialProducts />
              <TechnicalParameters />
              <Show onMobile onTablet>
                <SellerCard />
              </Show>
              <Disclaimer disclaimer={props.disclaimer} />
            </VStack>
            <Box width={['100%', '100%', 90]} flexShrink={0}>
              <Box position={['static', 'static', 'sticky']} top={SIDEBAR_STICKY_OFFSET}>
                <VStack spacing={[6, 6, 8]}>
                  <Sidebar />
                </VStack>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Center>
    </FinancialProductPreferenceProvider>
  );
}

function OnlineOfferPrint() {
  const {disclaimer, vehicle} = useGetOnlineOfferData();

  // The PDF is built from the deal vehicle; the backend refuses it for alternatives alone.
  if (isNil(vehicle)) {
    return <ErrorSection />;
  }

  return (
    <HelmetProvider>
      <Helmet titleTemplate={`%s • ${APPLICATION_NAME}`} htmlAttributes={{lang: i18n.language}}>
        <meta name="description" content={APPLICATION_NAME} />
      </Helmet>

      <Box maxWidth={278} width={278} backgroundColor="fill.white">
        <HeroPrint />
        <SpecificationPrint />
        <CarPassPrint />
        <DetailsPrint />
        <ElectricalParametersPrint />
        <AdvertisementDescriptionPrint />
        <WarrantyPrint />
        <FeaturesPrint />
        <CertificatePrint />
        <FinancialProductsPrint />
        <TechnicalParametersPrint />
        <ReservationCardPrint />
        <SellerCardPrint />
        <DisclaimerPrint disclaimer={disclaimer} />
      </Box>
    </HelmetProvider>
  );
}
